import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { qk } from "@/lib/query-keys";
import { QmFunnelSchema, type QmFunnel, type QmFunnelType } from "@/lib/validation";

export interface QmFunnelParams {
  /** ISO-8601 datetime with offset, e.g. 2026-05-19T00:00:00Z. Required. */
  from: string;
  /** ISO-8601 datetime with offset, e.g. 2026-06-11T23:59:59Z. Required. */
  to: string;
  /** Funnel definition to compute. Server default: "reservation". */
  funnel?: QmFunnelType;
  /** Max drop-off session ids returned per stage (0..50). Server default: 10. */
  dropOffSampleSize?: number;
}

/**
 * Builds the QM funnel querystring. Like the friction endpoint this has its own
 * param set (no granularity/env), so it does not reuse `toSearchParams`.
 * Optional params are omitted when unset so the server defaults apply.
 */
function toQmFunnelSearchParams(params: QmFunnelParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set("from", params.from);
  sp.set("to", params.to);
  if (params.funnel) sp.set("funnel", params.funnel);
  if (params.dropOffSampleSize !== undefined) {
    sp.set("dropOffSampleSize", String(params.dropOffSampleSize));
  }
  return sp;
}

export interface UseQmFunnelOptions {
  enabled?: boolean;
}

/**
 * Use Case A2 — reservation/quote page-path funnel from QuantumMetric, with
 * monotonic `reached` counts (source of truth) and per-stage drop-off session
 * ids for replay drill-down. Backed by `GET /api/v1/qm/funnel`.
 */
export function useQmFunnel(
  params: QmFunnelParams,
  options: UseQmFunnelOptions = {},
) {
  return useQuery<QmFunnel>({
    queryKey: qk.qm.funnel(params),
    queryFn: async ({ signal }) => {
      const raw = await api.get<unknown>(
        `/api/v1/qm/funnel?${toQmFunnelSearchParams(params).toString()}`,
        { signal },
      );
      return QmFunnelSchema.parse(raw);
    },
    enabled: options.enabled !== false,
    staleTime: 60_000,
  });
}
